import { useEffect, useRef, useState, type ImgHTMLAttributes, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import {
    Check,
    ExternalLink,
    FileText,
    Library,
    Link2,
    Mail,
    MapPin,
    MessageSquareText,
    Rocket,
    ShoppingCart,
    Star,
    Tag,
} from 'lucide-react';

import { userAPI } from '../UserAPI';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { storeHref } from './marketplaceFilters';
import PageFade from './PageFade';
import { MarketplaceOfferIcon } from '../real-gain/components/MarketplaceOfferNameCell';

type StoreOffer = any;

const DEFAULT_SECTION = 'description';

/** Only plain web links from offer data become anchors. */
const isWebUrl = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//i.test(value);

function formatFee(fee: number, language: string): string {
    return new Intl.NumberFormat(language, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: fee % 1 === 0 ? 0 : 2,
    }).format(fee);
}

function providerHue(name?: string): number {
    let hash = 0;
    const text = name || '';
    for (let index = 0; index < text.length; index += 1) {
        hash = (hash * 31 + text.charCodeAt(index)) % 360;
    }
    // 20 to 240 degrees (orange to blue): no pink or purple tones
    return 20 + (hash % 220);
}

function providerInitials(name?: string): string {
    const words = (name || '?').split(/\s+/).filter(Boolean);
    return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '?').slice(0, 2)).toUpperCase();
}

/**
 * Image with a skeleton until it has loaded. The skeleton fills the image's own
 * box once there is one; before that (no intrinsic size yet) `min-h` holds the
 * space so the page does not jump when the image arrives.
 */
function useImageLoadState() {
    const imageRef = useRef<HTMLImageElement>(null);
    const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading');

    // A cached image can be complete before React attaches `onLoad`.
    useEffect(() => {
        if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
            setState('loaded');
        }
    }, []);

    return {
        imageRef,
        state,
        handlers: {
            onLoad: () => setState('loaded'),
            onError: () => setState('error'),
        },
    };
}

/** Preview image above the description; stays wrapped in `Zoom`, so the skeleton is a sibling. */
function PreviewImage({ src, alt }: { src: string; alt: string }) {
    const { imageRef, state, handlers } = useImageLoadState();
    return (
        <div className={cn('relative min-w-0', state === 'loading' && 'min-h-[240px]')}>
            {state === 'loading' && <Skeleton aria-hidden className="absolute inset-0 h-full w-full rounded-lg" />}
            <Zoom>
                <img
                    ref={imageRef}
                    src={src}
                    alt={alt}
                    {...handlers}
                    className={cn(
                        'max-h-[360px] w-full rounded-lg border object-cover transition-opacity duration-300',
                        state === 'loading' ? 'opacity-0' : 'opacity-100',
                    )}
                />
            </Zoom>
        </div>
    );
}

/** `img` renderer for the markdown description (covers raw `<img>` from rehype-raw too). */
function MarkdownImage({ node: _node, className, onLoad: _onLoad, onError: _onError, ...props }: ImgHTMLAttributes<HTMLImageElement> & { node?: unknown }) {
    const { imageRef, state, handlers } = useImageLoadState();
    return (
        <span className={cn('relative my-2 block min-w-0', state === 'loading' && 'min-h-[200px]')}>
            {state === 'loading' && <Skeleton aria-hidden className="absolute inset-0 block h-full w-full rounded-lg" />}
            <img
                ref={imageRef}
                alt=""
                {...props}
                {...handlers}
                className={cn('transition-opacity duration-300', state === 'loading' ? 'opacity-0' : 'opacity-100', className)}
            />
        </span>
    );
}

const MARKDOWN_COMPONENTS = { img: MarkdownImage };

/** One labelled block of the info column. */
function InfoBlock({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <div className="text-xs font-medium text-muted-foreground">{label}</div>
            <div className="min-w-0 text-sm">{children}</div>
        </div>
    );
}

function StarsReadOnly({ value }: { value: number }) {
    return (
        <span className="inline-flex items-center gap-0.5" aria-hidden>
            {Array.from({ length: 5 }, (_, index) => (
                <Star
                    key={index}
                    size={14}
                    strokeWidth={1.5}
                    className={index < Math.round(value) ? 'fill-warning text-warning' : 'text-muted-foreground'}
                />
            ))}
        </span>
    );
}

/** `Real Estate/Report.pdf` becomes file name and folder. */
function splitPath(path: string): { name: string; folder: string } {
    const parts = path.split('/').filter(Boolean);
    const name = parts.pop() || path;
    return { name, folder: parts.join(' / ') };
}

export interface MarketplaceOfferPageProps {
    offer: StoreOffer | null;
    loading: boolean;
    canOrder: boolean;
    /** The offer belongs to the customer's own company: no order button, just a note. */
    owned: boolean;
    onTest: (offer: StoreOffer) => void;
    onOrder: (offer: StoreOffer) => void;
}

export default function MarketplaceOfferPage({ offer, loading, canOrder, owned, onTest, onOrder }: MarketplaceOfferPageProps) {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const topRef = useRef<HTMLDivElement>(null);
    const [linkCopied, setLinkCopied] = useState(false);

    const typeLabel = offer ? t(`agentPanel.store.types.${offer._type}.label`, { defaultValue: offer._type }) : '';

    if (loading) {
        return (
            <div ref={topRef} className="flex min-w-0 flex-col gap-6" aria-busy="true">
                <div className="flex min-w-0 items-start gap-4">
                    <Skeleton className="h-14 w-14 shrink-0 rounded-xl" />
                    <div className="flex flex-1 flex-col gap-2">
                        <Skeleton className="h-7 w-1/3" />
                        <Skeleton className="h-4 w-1/4" />
                    </div>
                    <Skeleton className="h-10 w-28" />
                </div>
                <div className="flex max-w-4xl flex-col gap-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-11/12" />
                    <Skeleton className="h-4 w-2/3" />
                </div>
                <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_240px]">
                    <div className="flex min-w-0 flex-col gap-4">
                        <Skeleton className="h-9 w-2/3" />
                        <Skeleton className="h-[240px] w-full rounded-lg" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-5/6" />
                    </div>
                    <div className="order-last flex flex-col gap-5 lg:border-l lg:pl-6">
                        {Array.from({ length: 4 }, (_, index) => (
                            <div key={index} className="flex flex-col gap-2">
                                <Skeleton className="h-3 w-16" />
                                <Skeleton className="h-4 w-3/4" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!offer) {
        return (
            <div ref={topRef} className="flex min-w-0 flex-col gap-6">
                <div className="flex flex-col items-center gap-2 py-16 text-center">
                    <div className="text-sm font-semibold">{t('agentPanel.store.offer.notFoundTitle')}</div>
                    <div className="max-w-prose text-sm text-muted-foreground">{t('agentPanel.store.offer.notFoundHint')}</div>
                </div>
            </div>
        );
    }

    const termsUrl = offer.urlTermsAndConditions || offer.termsAndConditionsURL;
    const privacyUrl = offer.urlPrivacyPolicy || offer.dataProtectionURL;
    const provider = offer.provider || {};
    const address = provider.address;
    const contactEmail = provider.email || offer.contactEmail;
    const plans: any[] = offer.plans || [];
    const questions: string[] = (offer.sampleRequests || []).filter((entry: unknown) => typeof entry === 'string' && entry.trim());
    const updated = offer.updated ? new Date(offer.updated) : null;
    const stats = offer.reportStats;
    const ratingCount = typeof stats?.count === 'number' ? stats.count : 0;

    // Documents of a knowledge base: uploaded files and managed files, de-duplicated by path.
    const documentsByPath = new Map<string, { path: string; updated?: string }>();
    [...(offer.files || []), ...(offer.managedFiles || [])].forEach((file: any) => {
        if (typeof file?.path === 'string') {
            const key = file.path.replace(/^\/+/, '');
            if (!documentsByPath.has(key)) {
                documentsByPath.set(key, { path: key, updated: file.updated });
            }
        }
    });
    const documents = Array.from(documentsByPath.values());
    const sourceLinks: string[] = (offer.links || []).map((link: any) => link?.url).filter(isWebUrl);

    const fees = plans.map((plan) => (typeof plan.baseFee === 'number' ? plan.baseFee : 0));
    const cheapest = fees.length > 0 ? Math.min(...fees) : null;
    const priceLabel = cheapest === null
        ? t('agentPanel.store.priceOnRequest')
        : cheapest === 0
            ? t('agentPanel.store.priceFree')
            : t('agentPanel.store.priceFrom', { amount: formatFee(cheapest, i18n.language) });

    const sections: { value: string; label: string; icon: typeof FileText; count?: number }[] = [
        { value: 'description', label: t('agentPanel.store.offer.sections.description'), icon: FileText },
        { value: 'pricing', label: t('agentPanel.store.offer.sections.pricing'), icon: Tag, count: plans.length },
        ...(questions.length > 0
            ? [{ value: 'questions', label: t('agentPanel.store.offer.sections.questions'), icon: MessageSquareText, count: questions.length }]
            : []),
        ...(documents.length > 0 || sourceLinks.length > 0
            ? [{ value: 'documents', label: t('agentPanel.store.offer.sections.documents'), icon: Library, count: documents.length + sourceLinks.length }]
            : []),
    ];
    const requestedSection = searchParams.get('section');
    const section = sections.some((entry) => entry.value === requestedSection) ? (requestedSection as string) : DEFAULT_SECTION;

    const selectSection = (value: string) => {
        const next = new URLSearchParams(searchParams);
        if (value === DEFAULT_SECTION) {
            next.delete('section');
        } else {
            next.set('section', value);
        }
        setSearchParams(next, { replace: true });
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setLinkCopied(true);
            window.setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            // Clipboard access can be denied; the address bar still holds the link.
        }
    };

    return (
        <div ref={topRef} className="flex min-w-0 scroll-mt-4 flex-col gap-6">

            <header className="flex min-w-0 flex-col gap-4">
                <div className="flex min-w-0 flex-wrap items-start gap-4">
                    <MarketplaceOfferIcon neutral offer={offer} className="h-14 w-14 shrink-0 rounded-xl border bg-card p-3" />
                    <div className="min-w-0 flex-1 basis-64">
                        <h2 className="break-words text-2xl font-bold tracking-tight">{offer.name}</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {typeLabel}
                            {provider.name && ` · ${t('agentPanel.store.by', { name: provider.name })}`}
                        </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {owned && (
                            <span className="inline-flex h-10 items-center gap-2 rounded-md border bg-muted px-4 text-sm font-medium text-muted-foreground">
                                <Check aria-hidden className="h-4 w-4" />
                                {t('agentPanel.store.owned')}
                            </span>
                        )}
                        {canOrder && !owned && (
                            <Button className="gap-2" onClick={() => onOrder(offer)}>
                                <ShoppingCart aria-hidden className="h-4 w-4" />
                                {t('settingsPanel.agentPanel.order')}
                            </Button>
                        )}
                        <Button variant="outline" className="gap-2" onClick={() => onTest(offer)}>
                            <Rocket aria-hidden className="h-4 w-4" />
                            {t('settingsPanel.agentPanel.test')}
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            aria-label={t('agentPanel.store.offer.copyLink')}
                            title={t('agentPanel.store.offer.copyLink')}
                            onClick={copyLink}
                        >
                            {linkCopied ? <Check aria-hidden className="h-4 w-4" /> : <Link2 aria-hidden className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>
                {offer.synopsis && (
                    <div className="min-w-0 max-w-4xl break-words text-sm leading-relaxed text-muted-foreground">{offer.synopsis}</div>
                )}
            </header>

            <PageFade fadeKey={offer._id}>
                <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1fr)_240px]">
                    <aside className="order-last flex min-w-0 flex-col gap-5 lg:border-l lg:pl-6">
                        <InfoBlock label={t('agentPanel.store.offer.pricing')}>
                            <button
                                type="button"
                                onClick={() => selectSection('pricing')}
                                className="rounded-sm text-left font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                                {priceLabel}
                            </button>
                        </InfoBlock>
                        <InfoBlock label={t('agentPanel.store.offer.rating')}>
                            {ratingCount > 0 ? (
                                <div className="flex items-center gap-2">
                                    <StarsReadOnly value={stats.averageRating} />
                                    <span className="text-muted-foreground">
                                        {Number(stats.averageRating).toLocaleString(i18n.language, { maximumFractionDigits: 1 })} ({ratingCount})
                                    </span>
                                </div>
                            ) : (
                                <span className="text-muted-foreground">{t('agentPanel.store.offer.noRatings')}</span>
                            )}
                        </InfoBlock>
                        <InfoBlock label={t('agentPanel.store.offer.provider')}>
                            <div className="flex min-w-0 flex-col gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                    <Avatar className="h-6 w-6 text-[10px]">
                                        <AvatarFallback
                                            className="font-semibold text-white"
                                            style={{ backgroundColor: `hsl(${providerHue(provider.name)} 45% 42%)` }}
                                        >
                                            {providerInitials(provider.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="min-w-0 break-words font-medium">{provider.name}</span>
                                </div>
                                {address && (address.street || address.city) && (
                                    <div className="flex items-start gap-2 text-muted-foreground">
                                        <MapPin aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                                        <span className="break-words">
                                            {[`${address.street || ''} ${address.streetNumber || ''}`.trim(), `${address.postalCode || ''} ${address.city || ''}`.trim()]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </span>
                                    </div>
                                )}
                                {contactEmail && (
                                    <a className="flex items-center gap-2 break-all text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" href={`mailto:${contactEmail}`}>
                                        <Mail aria-hidden className="h-4 w-4 shrink-0" />
                                        {contactEmail}
                                    </a>
                                )}
                                {isWebUrl(provider.website) && (
                                    <a className="flex items-center gap-2 break-all text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" href={provider.website} target="_blank" rel="noreferrer">
                                        <ExternalLink aria-hidden className="h-4 w-4 shrink-0" />
                                        {provider.website}
                                    </a>
                                )}
                            </div>
                        </InfoBlock>
                        <InfoBlock label={t('agentPanel.store.offer.category')}>
                            <Link
                                to={storeHref({ category: offer._type })}
                                className="inline-flex rounded-md border bg-card px-2 py-0.5 text-xs text-foreground hover:border-foreground/40"
                            >
                                {typeLabel}
                            </Link>
                        </InfoBlock>
                        {updated && !Number.isNaN(updated.getTime()) && (
                            <InfoBlock label={t('agentPanel.store.offer.lastUpdated')}>
                                {updated.toLocaleDateString(i18n.language)}
                            </InfoBlock>
                        )}
                        {(isWebUrl(termsUrl) || isWebUrl(privacyUrl)) && (
                            <InfoBlock label={t('agentPanel.store.offer.legal')}>
                                <div className="flex flex-col gap-1">
                                    {isWebUrl(termsUrl) && (
                                        <a className="text-foreground underline-offset-4 hover:underline" href={termsUrl} target="_blank" rel="noreferrer">
                                            {t('ui.termsAndConditions')}
                                        </a>
                                    )}
                                    {isWebUrl(privacyUrl) && (
                                        <a className="text-foreground underline-offset-4 hover:underline" href={privacyUrl} target="_blank" rel="noreferrer">
                                            {t('ui.dataProtection')}
                                        </a>
                                    )}
                                </div>
                            </InfoBlock>
                        )}
                    </aside>

                    <Tabs value={section} onValueChange={selectSection} className="min-w-0">
                        <TabsList>
                            {sections.map(({ value, label, icon: Icon, count }) => (
                                <TabsTrigger key={value} value={value}>
                                    <Icon aria-hidden className="h-4 w-4 shrink-0" />
                                    {label}
                                    {typeof count === 'number' && count > 0 && (
                                        <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">{count}</span>
                                    )}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value="description" className="mt-6 flex min-w-0 data-[state=inactive]:hidden flex-col gap-4">
                            {offer.images && offer.images.length > 0 && (
                                <PreviewImage src={userAPI.getResourceURL(offer.images[0])} alt={offer.name} />
                            )}
                            {offer.marketingContent ? (
                                <div className="min-w-0 break-words text-sm leading-relaxed [&_a]:underline [&_a]:underline-offset-4 [&_h1]:mt-4 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mt-4 [&_h2]:text-base [&_h2]:font-semibold [&_img]:!h-auto [&_img]:!max-w-full [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:overflow-x-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_ul]:list-disc [&_ul]:pl-5">
                                    <Markdown rehypePlugins={[rehypeRaw] as any} remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{offer.marketingContent}</Markdown>
                                </div>
                            ) : (
                                offer.description && <p className="max-w-prose text-sm leading-relaxed">{offer.description}</p>
                            )}
                        </TabsContent>

                        <TabsContent value="pricing" className="mt-6 min-w-0">
                            {plans.length === 0 ? (
                                <p className="text-sm text-muted-foreground">{t('agentPanel.store.priceOnRequest')}</p>
                            ) : (
                                <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                    {plans.map((plan, index) => {
                                        const fee = typeof plan.baseFee === 'number' ? plan.baseFee : 0;
                                        const billings: any[] = plan.billings || [];
                                        return (
                                            <div key={plan._id || index} className="flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-4 shadow-sm">
                                                <div className="text-sm font-semibold">{plan.name}</div>
                                                <div className="text-xl font-bold">
                                                    {fee === 0
                                                        ? t('agentPanel.store.priceFree')
                                                        : t('agentPanel.store.offer.perMonth', { amount: formatFee(fee, i18n.language) })}
                                                </div>
                                                {typeof plan.term === 'number' && plan.term > 1 && (
                                                    <div className="text-xs text-muted-foreground">
                                                        {t('agentPanel.store.offer.term', { months: plan.term })}
                                                    </div>
                                                )}
                                                {plan.description && (
                                                    <p className="text-xs leading-snug text-muted-foreground">{plan.description}</p>
                                                )}
                                                {billings.length > 0 && (
                                                    <ul className="mt-1 flex flex-col gap-1 border-t pt-2 text-xs">
                                                        {billings.map((billing, billingIndex) => (
                                                            <li key={billingIndex} className="flex justify-between gap-2">
                                                                <span className="min-w-0 text-muted-foreground">{billing.name}</span>
                                                                {typeof billing.fee === 'number' && (
                                                                    <span className="shrink-0 font-medium">{formatFee(billing.fee, i18n.language)}</span>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </TabsContent>

                        {questions.length > 0 && (
                            <TabsContent value="questions" className="mt-6 min-w-0">
                                <p className="mb-3 text-sm text-muted-foreground">{t('agentPanel.store.offer.questionsHint')}</p>
                                <ul className="flex max-w-3xl flex-col gap-2">
                                    {questions.map((question, index) => (
                                        <li key={index} className="flex items-start gap-3 rounded-lg border bg-card p-3 text-sm">
                                            <MessageSquareText aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                            <span className="min-w-0 break-words">{question}</span>
                                        </li>
                                    ))}
                                </ul>
                            </TabsContent>
                        )}

                        {(documents.length > 0 || sourceLinks.length > 0) && (
                            <TabsContent value="documents" className="mt-6 flex min-w-0 data-[state=inactive]:hidden flex-col gap-6">
                                {documents.length > 0 && (
                                    <ul className="flex max-w-3xl flex-col divide-y rounded-lg border bg-card">
                                        {documents.map((file) => {
                                            const { name, folder } = splitPath(file.path);
                                            const fileUpdated = file.updated ? new Date(file.updated) : null;
                                            return (
                                                <li key={file.path} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                                                    <FileText aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate" title={name}>{name}</div>
                                                        {folder && <div className="truncate text-xs text-muted-foreground">{folder}</div>}
                                                    </div>
                                                    {fileUpdated && !Number.isNaN(fileUpdated.getTime()) && (
                                                        <span className="shrink-0 text-xs text-muted-foreground">
                                                            {fileUpdated.toLocaleDateString(i18n.language)}
                                                        </span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                                {sourceLinks.length > 0 && (
                                    <div className="flex max-w-3xl flex-col gap-2">
                                        <h3 className="text-sm font-semibold">{t('agentPanel.store.offer.sources')}</h3>
                                        <ul className="flex flex-col gap-1 text-sm">
                                            {sourceLinks.map((url) => (
                                                <li key={url}>
                                                    <a className="inline-flex items-center gap-2 break-all text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" href={url} target="_blank" rel="noreferrer">
                                                        <ExternalLink aria-hidden className="h-3.5 w-3.5 shrink-0" />
                                                        {url}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </TabsContent>
                        )}
                    </Tabs>
                </div>
            </PageFade>
        </div>
    );
}
